import { Alert, Button, Card, Form, Input, Layout, Typography } from 'antd';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../services/auth';
import { getErrorMessage } from '../../utils/helpers';
import { DomainUtils } from '../../utils/domain';

const { Content } = Layout;
const { Title, Text } = Typography;

export const ForgotPassword: React.FC = () => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);
      setError(null);
      await authService.forgotPassword(values.email);
      setEmailSent(true);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to send password reset email'));
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <Content style={{ padding: '24px', maxWidth: 400, margin: '0 auto' }}>
        <Card>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
            Check Your Email
          </Title>

          <Alert
            message='Password Reset Email Sent'
            description='If your email is registered with us, you will receive a password reset link shortly. Please check your inbox and follow the instructions to reset your password.'
            type='success'
            showIcon
            style={{ marginBottom: 24 }}
          />

          <div style={{ textAlign: 'center' }}>
            <Text type='secondary'>
              Didn't receive the email? Check your spam folder or{' '}
              <Button type='link' onClick={() => setEmailSent(false)} style={{ padding: 0 }}>
                try again
              </Button>
            </Text>
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Text type='secondary'>
              Remember your password?{' '}
              <Link
                to={DomainUtils.getSubdomain() ? `${DomainUtils.getAppRootUrl()}/login` : '/login'}
              >
                Back to Login
              </Link>
            </Text>
          </div>
        </Card>
      </Content>
    );
  }

  return (
    <Content style={{ padding: '24px', maxWidth: 400, margin: '0 auto' }}>
      <Card>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
          Forgot Password
        </Title>

        <Text type='secondary' style={{ display: 'block', textAlign: 'center', marginBottom: 24 }}>
          Enter your email address and we'll send you a link to reset your password.
        </Text>

        {error && (
          <Alert
            message='Error'
            description={error}
            type='error'
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Form form={form} layout='vertical' onFinish={handleSubmit}>
          <Form.Item
            name='email'
            label='Email'
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input placeholder='Enter your email address' />
          </Form.Item>

          <Form.Item>
            <Button type='primary' htmlType='submit' block loading={isLoading}>
              Send Reset Link
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Text type='secondary'>
            Remember your password?{' '}
            <Link
              to={DomainUtils.getSubdomain() ? `${DomainUtils.getAppRootUrl()}/login` : '/login'}
            >
              Back to Login
            </Link>
          </Text>
        </div>
      </Card>
    </Content>
  );
};
